/* eslint-disable class-methods-use-this */
/* eslint-disable no-undef, new-cap */
// L dan windyInit global dari script tag, bukan import.

import SeriesSvc from '@/services/SeriesSvc';
import { convertDecimalToDMS } from '@/views/map/utils/coordinates';
import markerOnline from '@/assets/images/map/online-marker.svg';
import { getRpmTypeLabel } from '@/util/ship/fms-summary';
import { formatDateTime } from '@/util/date';
import { DISPLAY_DATETIME } from '@/util/apiDatetime';
import { hourWindowLabel } from '@/util/trail/trailData';
import { visibleTrailPoints } from '@/util/rangeDisplay';
import { nearestTrailPoint } from '@/util/trail/trailCursor';
import { positionAt, pathUpTo } from '@/util/trail/trailPlayback';
import { clusterByGrid } from '@/util/trail/mapCluster';
import { speedBuckets, bucketColors, bucketOf } from '@/util/trail/trailColor';
import { subscribe, publish, HOVER } from '@/util/chart/chartSync';
import { routeMarkerPoints, escapeHtml } from '@/util/trail/voyageMapExtras';
import {
  buildIconConfig,
  getMapIconStyle,
  setMapIconStyle as persistMapIconStyle,
  getMapIconScale,
  setMapIconScale as persistMapIconScale,
  scaleIconSize,
  headingIconUrlFor,
  MARKER_ICON_BASE_SIZE,
  HEADING_ICON_BASE_SIZE,
} from '@/util/ship/shipIcons';
import detailedIconSample from '@/assets/images/map/tug-boat-icon.svg';
import simpleIconSample from '@/assets/images/map/online-ship.svg';
import { formatNum } from '@/util/number';
import {
  buildDetailGroups,
  buildTrailStats,
  statusLabel as shipStatusLabel,
  statusColor as shipStatusColor,
  TRAIL_DAY_OPTIONS,
  describeLocation,
} from '@/util/ship/vesselDetail';
import { trackColor, TRACK_COLORS } from '@/util/ship/shipStatus';
import { shipClusterSvg } from '@/util/ship/shipClusterIcon';
import { buildDotTrailGroups, kmhToKnots } from '@/util/trail/dotTrailGroups';
import { mergeSatSeries } from '@/views/map/utils/satTrailSeries';
import { POLYLINE_MEASURE_OPTIONS } from './polylineMeasureOptions';
import TimezoneLayer from './timezoneLayer';
import LandmarkLayer from './landmarkLayer';
import { wireWindyControls } from './windyControls';
import { normalizeHost } from './mapHostContract';
import {
  renderStatusBadge,
  renderDetailPager,
  renderGroupBody,
  createSatDotTrailPopupContent,
} from '../popup/popupHtml';

const SHIP_FOCUS_ZOOM = 12;

// Titik dot mode sat selalu pakai ikon online.
function satDotIcon() {
  return L.icon({
    iconUrl: markerOnline,
    iconSize: scaleIconSize(MARKER_ICON_BASE_SIZE),
    className: 'marker-icon',
  });
}

// lat/lng bisa angka atau fungsi.
function latLngParts(latLng) {
  const lat = typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat;
  const lng = typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng;
  return { lat, lng };
}

class MapService {
  constructor(vueComponent) {
    this.host = normalizeHost(vueComponent);

    // Dibaca dari atribut onclick popup sat. map/index.vue nulis ulang ini pas mounted.
    window.mapServiceVm = vueComponent;

    // State internal layanan
    this.map = null;
    this.shipsLayerGroups = {};
    this.shipMarkers = {};
    this.shipHeadingMarkers = {};
    this.playbackState = null;
    this.highlightedMassId = null;
    this.trail = {};

    this.trailSegments = {};
    this.dotTrail = {};
    // Titik mentah dot trail per kapal.
    this.dotTrailPoints = {};
    this.dotTrailSyncTimeout = null;
    this.cursorMarkers = {};
    this.cursorUnsub = null;
    this.routeMarkerLayer = null;
    this.trailCheckboxStatus = {};
    this.trailDays = {};
    // rentang yang dipakai waktu trail tiap kapal difetch, buat deteksi filter tanggal ganti
    this.trailRangeKey = {};
    // massId yang trailnya lagi difetch
    this.trailFetching = {};
    // Trail yang paling akhir dinyalain; penggeser waktu ngikut ini.
    this.lastTrailMassId = null;
    this.firstRender = true;
    // refreshShips bisa keulang selagi addShipsToMap masih nunggu trail.
    this.refreshing = false;
    this.refreshPending = false;
    // true kalau trail belum sempat difit pas render pertama
    this.pendingTrailFit = false;
    // true selama kontrol ukur jarak aktif; picker cuaca Windy nahan diri selama ini
    this.measuring = false;
    // cache lokal detail titik dot trail - sengaja gak di Vuex biar commit gak trigger rebuild map & nutup popup
    this.pointDetail = {};
    this.openPopupMassId = null;
    this.currentPopupPage = 1;
    this.currentDotTrailPopupPage = 1;
    this.totalPopupPages = 3;
    this.landmark = null;
    this.shipClusterPx = 80;
    this.shipClusterLayer = null;
    this.shipClusterTimeout = null;
    this.baseLayers = {};
    this.activeBaseLayerKey = null;
    this.activeBaseLayer = null;
    this.mapSettingsPanel = null;
    this.resizeObserver = null;
    this.resizeRaf = null;
    this.timezone = null;
    this.initialView = null;
    this.onMapMoved = null;
    this.fullscreenHandler = null;
    this.pendingPopupMassId = null;
  }

  // --- Inisialisasi dan Penghancuran Peta ---

  initializeDefaultMap(elementId) {
    if (this.map || !document.getElementById(elementId)) return;

    const first = this.initialView;
    const map = L.map(elementId).setView(
      first ? [first.lat, first.lng] : [-6.2, 106.816666],
      first ? first.zoom : 13,
      {
        animate: true,
        pan: {
          duration: 1,
        },
      },
    );
    map.setMinZoom(5);
    map.setMaxZoom(19);

    const googleLayer = L.tileLayer('https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&gl=id&hl=id', {
      maxZoom: 19,
      attribution: 'Map data © Google Maps',
    });
    const googleLayerSat = L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&gl=id&hl=id', {
      maxZoom: 19,
      attribution: 'Map data © Google Maps',
    });
    const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    });

    this.map = map;
    this.baseLayers = {
      satellite: googleLayerSat,
      vectorColor: googleLayer,
      vectorWhite: osmLayer,
    };
    const storedKey = localStorage.getItem('activeBaseLayer') || 'satellite';
    this.setBaseLayer(this.baseLayers[storedKey] ? storedKey : 'satellite');

    this.attachCommonMapBehaviour(elementId);
  }

  // Setup yang sama buat peta default & peta Windy. Dipanggil sesudah `this.map`
  // keisi. Base layer sengaja gak di sini — itu cuma punya jalur default, di Windy
  // base map-nya punya Windy.
  attachCommonMapBehaviour(elementId) {
    const { map } = this;

    L.control.polylineMeasure(POLYLINE_MEASURE_OPTIONS).addTo(map);

    // `:toggle` fire pas kontrolnya dinyalain/dimatiin — `:start` baru fire pas
    // titik pertama digambar, kelewat telat buat nahan picker di klik yang sama.
    map.on('polylinemeasure:toggle', (e) => { this.measuring = !!e.status; });

    // Menghubungkan event peta kembali ke komponen Vue
    map.on('polylinemeasure:start', () => this.host.onPolylineStart());
    map.on('polylinemeasure:change', (e) => this.host.onPolylineChange(e));
    map.on('polylinemeasure:clear', () => this.host.onPolylineClear());
    map.on('polylinemeasure:finish', () => this.host.onPolylineFinish());
    this.timezone = new TimezoneLayer({ map, onChange: () => this.syncMapSettingsUI() });
    this.landmark = new LandmarkLayer({
      map,
      store: this.host.store,
      onChange: () => this.syncMapSettingsUI(),
    });

    if (!this.host.hideSettingsControl) {
      this.addMapSettingsControl();
    }
    this.addFullscreenControl();
    map.on('moveend', () => this.handleLandmarkViewportChange());
    map.on('zoomend', () => this.handleLandmarkViewportChange());
    map.on('moveend', () => this.scheduleDotTrailSync());
    map.on('zoomend', () => this.scheduleDotTrailSync());
    this.cursorUnsub = subscribe(this.hoverChannel(), (ms) => this.moveCursor(ms));
    map.on('moveend', () => this.scheduleShipClusterSync());
    map.on('zoomend', () => this.scheduleShipClusterSync());
    this.setupPopupDelegation(map);
    this.addShipsToMap({ fitMapToBounds: !this.initialView });
    this.renderRouteMarkers();
    if (!this.host.ships || this.host.ships.length === 0) this.fitToRouteMarkers();
    map.on('moveend', () => {
      if (!this.onMapMoved) return;
      const center = map.getCenter();
      this.onMapMoved(center.lat, center.lng, map.getZoom());
    });
    this.setupResizeObserver(elementId);
  }

  // Keep the Leaflet map in sync when its container resizes (e.g. the Home
  // sidebar pushing the map narrower/wider) so tiles re-render correctly.
  setupResizeObserver(elementId) {
    if (typeof window.ResizeObserver === 'undefined') return;
    const element = document.getElementById(elementId);
    if (!element) return;
    this.resizeObserver = new window.ResizeObserver(() => {
      if (this.resizeRaf) window.cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = window.requestAnimationFrame(() => {
        if (this.map) this.map.invalidateSize({ animate: false });
      });
    });
    this.resizeObserver.observe(element);
  }

  initializeWindyMap(elementId) {
    try {
      windyInit({
        key: 'R1CTVY4LQHEtYMnM4GPV8WAFq17eFPHg',
        lat: 0,
        lon: 0,
        zoom: 10,
      }, (windyAPI) => {
        this.map = windyAPI.map;
        const {
          picker, store, overlays, broadcast,
        } = windyAPI;

        wireWindyControls({
          picker, store, overlays, broadcast,
        });

        // `this.map` udah diisi windyAPI.map di awal callback. Variabel `map` gak
        // pernah ada di sini — ini yang bikin ReferenceError dan matiin jalur windy.
        this.map.on('click', (e) => {
          if (this.measuring) return;
          const { lat, lng } = e.latlng;
          picker.open({ lat, lon: lng });
        });

        this.attachCommonMapBehaviour(elementId);
      });
    } catch (error) {
      console.error('Error initializing Windy map:', error);
    }
  }

  destroy() {
    if (this.cursorUnsub) {
      this.cursorUnsub();
      this.cursorUnsub = null;
    }
    this.cursorMarkers = {};
    this.routeMarkerLayer = null;
    if (this.map) {
      this.map.off();
      this.map.remove();
    }
    this.map = null;
    this.shipsLayerGroups = {};
    this.shipMarkers = {};
    this.shipHeadingMarkers = {};
    this.playbackState = null;
    this.shipClusterLayer = null;
    if (this.shipClusterTimeout) {
      clearTimeout(this.shipClusterTimeout);
      this.shipClusterTimeout = null;
    }
    this.highlightedMassId = null;
    this.trail = {};

    this.trailSegments = {};
    this.dotTrail = {};
    this.dotTrailPoints = {};
    clearTimeout(this.dotTrailSyncTimeout);
    this.dotTrailSyncTimeout = null;
    this.trailDays = {};
    this.trailRangeKey = {};
    this.trailFetching = {};
    this.lastTrailMassId = null;
    this.firstRender = true;
    this.refreshing = false;
    this.refreshPending = false;
    this.pendingTrailFit = false;
    this.measuring = false;
    this.pointDetail = {};
    this.openPopupMassId = null;
    this.baseLayers = {};
    this.activeBaseLayerKey = null;
    this.activeBaseLayer = null;
    this.mapSettingsPanel = null;
    if (this.fullscreenHandler) {
      document.removeEventListener('fullscreenchange', this.fullscreenHandler);
      this.fullscreenHandler = null;
    }
    this.onMapMoved = null;
    this.initialView = null;
    this.pendingPopupMassId = null;
    if (this.landmark) {
      this.landmark.destroy();
      this.landmark = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeRaf) {
      window.cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }
    if (this.timezone) {
      this.timezone.destroy();
      this.timezone = null;
    }
    // Clean up window reference
    window.mapServiceVm = null;
  }

  // --- Logika Penambahan Kapal dan Marker ---

  // massId dari id DOM berupa string, jadi dibandingin sebagai string.
  findShip(massId) {
    return this.host.ships.find((s) => String(s.massId) === String(massId));
  }

  async addShipsToMap({ fitMapToBounds = true, reopenMassId = null } = {}) {
    if (!this.map) return;

    const trailPromises = [];
    this.shipMarkers = {};
    this.shipHeadingMarkers = {};

    this.host.ships.forEach((ship) => {
      const shipLayerGroup = L.layerGroup();
      this.shipsLayerGroups[ship.massId] = shipLayerGroup;

      // iconConfig cuma diisi getLastData / fetchAndReconcileAssets. Halaman yang
      // gak manggil keduanya (mis. Report) dapet ship tanpa ikon, dan tanpa ini
      // markerOptions.icon jadi undefined -- yang NIMPA default Leaflet, bikin
      // _initIcon lempar "Cannot read properties of undefined (reading
      // 'createIcon')" dan kapalnya gak pernah kegambar.
      if (!ship.iconConfig) {
        const built = buildIconConfig(ship, ship.shipStatus);
        ship.iconConfig = built.iconConfig;
        ship.markerIconUrl = ship.markerIconUrl || built.markerIconUrl;
      }

      // Ikon nempel di objek ship & keikut pas status berubah - bandingin url-nya
      // biar warnanya ikut status baru, bukan sisa status lama.
      if (ship.iconConfig && ship.icon?.options?.iconUrl !== ship.iconConfig.iconUrl) {
        ship.icon = L.icon({
          iconUrl: ship.iconConfig.iconUrl,
          iconSize: ship.iconConfig.iconSize,
          popupAnchor: [0, -41],
          tooltipAnchor: [0, 0],
          className: 'ship-icon',
        });
      }

      if (ship.markerIconUrl && ship.markerIcon?.options?.iconUrl !== ship.markerIconUrl) {
        ship.markerIcon = L.icon({
          iconUrl: ship.markerIconUrl,
          iconSize: scaleIconSize(MARKER_ICON_BASE_SIZE),
          className: 'marker-icon',
        });
      }

      // Create heading icon for non-default assets
      if (ship.iconConfig?.hasHeadingIcon) {
        const headingIconUrl = headingIconUrlFor(ship.shipStatus);

        if (ship.headingIcon?.options?.iconUrl !== headingIconUrl) {
          ship.headingIcon = L.icon({
            iconUrl: headingIconUrl,
            iconSize: scaleIconSize(HEADING_ICON_BASE_SIZE),
            className: 'heading-icon',
          });
        }
      }

      const headingMarker = this.createShipHeadingMarker(ship);
      const marker = this.createShipMarker(ship);

      if (!marker) {
        // voyage: kapal tanpa posisi tetep gambar trail rentangnya
        if (this.host.options.routeMarkers) {
          if (ship.isVisible !== false) shipLayerGroup.addTo(this.map);
          this.queueTrail(ship, trailPromises);
        }
        return;
      }

      if (ship.headingIcon) {
        shipLayerGroup.addLayer(headingMarker);
        this.shipHeadingMarkers[ship.massId] = headingMarker;
      }
      shipLayerGroup.addLayer(marker);
      this.shipMarkers[ship.massId] = marker;

      // `!== false`, bukan truthy: isVisible cuma diisi getLastData /
      // fetchAndReconcileAssets, jadi halaman yang gak manggil itu (mis. Report)
      // dapet undefined dan kapalnya gak pernah kegambar.
      if (ship.isVisible !== false) {
        shipLayerGroup.addTo(this.map);
      }

      // Build popup content lazily on each open so it reflects the latest ship
      // data and starts on page 1. Leaflet opens the bound popup on marker click.
      marker.bindPopup(() => {
        this.currentPopupPage = 1;
        const currentShip = this.findShip(ship.massId) || ship;
        return this.createPopupContent(currentShip);
      }, {
        maxWidth: 400,
        closeButton: true,
        autoPan: true,
      });

      marker.on('popupopen', () => {
        this.openPopupMassId = ship.massId;
        const currentShip = this.findShip(ship.massId) || ship;
        this.ensureShipSummary(currentShip);
      });
      marker.on('popupclose', () => {
        if (this.openPopupMassId === ship.massId) this.openPopupMassId = null;
      });

      this.queueTrail(ship, trailPromises);

      // Kalo popup kapal ini lagi kebuka pas map di-rebuild (mis. abis fetch trail/pulse), buka lagi
      // di marker baru biar popup-nya ga ilang & ke-render ulang dari ship terbaru (yg udah ada summary).
      if (reopenMassId !== null && ship.massId === reopenMassId) {
        marker.openPopup();
      }
    });

    // Wait for all trails to be loaded
    await Promise.all(trailPromises);

    if (fitMapToBounds) this.fitMapToBounds();
    this.firstRender = false;
    this.syncShipClusters();
    if (this.pendingPopupMassId !== null && this.shipMarkers[this.pendingPopupMassId]) {
      this.shipMarkers[this.pendingPopupMassId].openPopup();
      this.pendingPopupMassId = null;
    }
    if (this.playbackState) {
      this.playbackAt(this.playbackState.massId, this.playbackState.ms);
    }
  }

  queueTrail(ship, trailPromises) {
    const optionEnable = this.host.options.enableTrail === 'true' || this.host.options.enableTrail === true;
    // Pertahanin trail yg udah dinyalain user pas refresh/redraw (jangan ketimpa balik ke option).
    const enableTrail = this.trailCheckboxStatus[ship.massId] === true || optionEnable;
    this.trailCheckboxStatus[ship.massId] = enableTrail;
    if (enableTrail) {
      // Redraw dari cache (forceRefetch=false) & jangan fitBounds biar map ga loncat tiap pulse.
      trailPromises.push(this.toggleTrail(true, ship, this.trailDays[ship.massId] || 1, false, false));
    }
  }

  // Lupain rentang trail yg lagi kegambar biar redraw berikutnya nge-fetch ulang.
  // Dipanggil sebelum getLastData: SET_ASSETS-nya udah nendang refreshShips, jadi
  // trailnya kebawa tanpa rebuild peta tambahan.
  expireTrails() {
    Object.keys(this.trailCheckboxStatus).forEach((massId) => {
      if (this.trailCheckboxStatus[massId]) delete this.trailRangeKey[massId];
    });
  }

  // Kapal yang udah gak ada di daftar harus ilang juga jejaknya; kalo enggak
  // firstActiveTrail masih nunjuk kapal lama dan penggeser waktunya salah kapal.
  dropMissingShips() {
    const stillListed = new Set((this.host.ships || []).map((s) => String(s.massId)));
    const dropped = Object.keys(this.shipsLayerGroups).filter((id) => !stillListed.has(String(id)));
    dropped.forEach((massId) => {
      const layerGroup = this.shipsLayerGroups[massId];
      if (this.map.hasLayer(layerGroup)) this.map.removeLayer(layerGroup);
      layerGroup.clearLayers();
      this.removeCursor(massId);
      if (this.lastTrailMassId === String(massId)) this.lastTrailMassId = null;
      delete this.shipsLayerGroups[massId];
      delete this.shipMarkers[massId];
      delete this.shipHeadingMarkers[massId];
      delete this.trail[massId];
      delete this.trailSegments[massId];
      delete this.dotTrail[massId];
      delete this.dotTrailPoints[massId];
      delete this.trailCheckboxStatus[massId];
      delete this.trailDays[massId];
      delete this.trailRangeKey[massId];
    });
    if (dropped.length) this.notifyActiveTrail();
  }

  async refreshShips() {
    if (!this.map) return;

    // Yang nyusul cukup nitip; jangan ikut bongkar layer yang lagi diisi.
    if (this.refreshing) {
      this.refreshPending = true;
      return;
    }
    this.refreshing = true;

    try {
      do {
        this.refreshPending = false;

        // Simpen popup yg lagi kebuka SEBELUM teardown (teardown bakal trigger popupclose -> null).
        const reopenMassId = this.openPopupMassId;

        this.dropMissingShips();

        // Clear all existing ship layer groups
        Object.keys(this.shipsLayerGroups).forEach((massId) => {
          const layerGroup = this.shipsLayerGroups[massId];
          if (this.map.hasLayer(layerGroup)) {
            this.map.removeLayer(layerGroup);
          }
          layerGroup.clearLayers();
          // clearLayers udah ngelepas polyline/dot; buang ref-nya biar guard redraw mulai bersih.
          delete this.trail[massId];
          delete this.trailSegments[massId];
          delete this.dotTrail[massId];
        });

        // Re-add all ships with updated data (buka lagi popup yg tadi kebuka)
        // eslint-disable-next-line no-await-in-loop
        await this.addShipsToMap({ fitMapToBounds: false, reopenMassId });
      } while (this.refreshPending);
    } finally {
      this.refreshing = false;
    }
  }

  toggleShipVisibility(massId, isVisible) {
    const layerGroup = this.shipsLayerGroups[massId];
    if (!layerGroup) return;

    if (isVisible) {
      if (!this.map.hasLayer(layerGroup)) {
        this.map.addLayer(layerGroup);
      }
    } else if (this.map.hasLayer(layerGroup)) {
      this.map.removeLayer(layerGroup);
    }
    this.syncShipClusters();
  }

  showAllShips() {
    if (!this.map) return;
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const massId in this.shipsLayerGroups) {
      const layerGroup = this.shipsLayerGroups[massId];
      if (!this.map.hasLayer(layerGroup)) {
        this.map.addLayer(layerGroup);
      }
    }
    this.syncShipClusters();
  }

  hideAllShips() {
    if (!this.map) return;
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const massId in this.shipsLayerGroups) {
      const layerGroup = this.shipsLayerGroups[massId];
      if (this.map.hasLayer(layerGroup)) {
        this.map.removeLayer(layerGroup);
      }
    }
    this.syncShipClusters();
  }

  createShipMarker(ship) {
    const { lat, lng } = latLngParts(ship.lastLatLng);

    if (lat === undefined || lng === undefined) {
      return null;
    }

    const latLng = L.latLng(lat, lng);

    // `icon` cuma diisi kalau ada: ngirim undefined bukan berarti "pakai default",
    // tapi nimpa default-nya jadi undefined.
    const markerOptions = { zIndexOffset: 2000 };
    if (ship.icon) markerOptions.icon = ship.icon;

    // Ikon detailed punya penunjuk arah sendiri; badan kapalnya tetap tegak.
    if (!ship.headingIcon) {
      markerOptions.rotationAngle = ship.lastGpsData?.track || 0;
      markerOptions.rotationOrigin = '50% 50%';
    }

    const marker = L.marker(latLng, markerOptions);

    let tooltipClass;
    if (ship.shipStatus) {
      tooltipClass = `ship-${ship.shipStatus}`;
    } else {
      tooltipClass = ship.dataStatus && ship.lastGpsData ? 'ship-online' : 'ship-offline';
    }
    // Label lurus di tengah bawah ikon. Font & jaraknya ikut skala biar proporsional
    // (padding/radius pake em di CSS jadi ikut font). Offset y = setengah tinggi ikon
    // (udah ke-skala) + gap kecil, biar nempel pas di bawah ikon apapun ukurannya.
    const labelScale = getMapIconScale();
    const iconHeight = ship.iconConfig?.iconSize?.[1] ?? 56 * labelScale;
    marker.bindTooltip(
      `<div class="ship-label ${tooltipClass}" style="font-size:${(13 * labelScale).toFixed(1)}px">${ship.massName}</div>`,
      { permanent: true, direction: 'bottom', offset: [0, iconHeight / 2 + 4 * labelScale] },
    ).openTooltip();
    return marker;
  }

  createShipHeadingMarker(ship) {
    // Extract lat/lng to avoid Vue reactivity issues
    const { lat, lng } = latLngParts(ship.lastLatLng);
    const latLng = L.latLng(lat, lng);

    return L.marker(latLng, {
      icon: ship.headingIcon,
      // Heading marker is only visual overlay; clicks should reach the ship marker popup below it.
      interactive: false,
      rotationAngle: ship.lastGpsData?.track || 0,
      rotationOrigin: '50% 50%',
      zIndexOffset: 2100, // Keep heading marker above ship marker and trails
    });
  }

  fitMapToBounds() {
    if (!this.map) return;
    if (this.host.options.fitToTrail) {
      this.fitToTrailOnce();
      return;
    }
    if (this.host.ships.length === 0) return;

    const visibleShips = this.host.ships.filter((ship) => ship.isVisible !== false && ship.lastLatLng);
    if (visibleShips.length === 0) return;

    if (visibleShips.length === 1) {
      this.map.setView(visibleShips[0].lastLatLng, 9, {
        animate: true,
        pan: {
          duration: 1,
        },
      });
    } else {
      const group = new L.featureGroup(
        visibleShips.map((ship) => L.marker(ship.lastLatLng)),
      );
      this.map.fitBounds(group.getBounds(), { padding: [100, 100] });
    }
  }

  // Render pertama aja: fit ke jejak, kalo jejak belum ada tunggu 'ready' pertama.
  fitToTrailOnce() {
    const layers = Object.values(this.trail).filter(Boolean);
    if (layers.length) {
      this.pendingTrailFit = false;
      const fit = this.host.options.fitToTrail;
      // sisain ruang buat kontrol kiri, marker A/B, dan playback bawah (di HP playback 2 baris)
      const padding = typeof fit === 'object' ? fit : { paddingTopLeft: [70, 50], paddingBottomRight: [30, this.scrubberSpace()] };
      this.map.fitBounds(L.featureGroup(layers).getBounds(), padding);
      return;
    }
    this.pendingTrailFit = true;
    this.fitToRouteMarkers();
  }

  scrubberSpace() {
    const container = this.map.getContainer();
    const scrubber = container.parentElement && container.parentElement.querySelector('.trail-scrubber');
    if (!scrubber) return 90;
    const gap = container.getBoundingClientRect().bottom - scrubber.getBoundingClientRect().top;
    return Math.max(90, Math.round(gap) + 10);
  }

  // --- Trail & dot trail ---

  async toggleTrail(isChecked, ship, days = 1, forceRefetch = false, fitBounds = true) {
    this.trailCheckboxStatus[ship.massId] = isChecked;
    // kapal ber-trail keluar/masuk cluster
    this.scheduleShipClusterSync();
    if (isChecked) {
      this.trailDays[ship.massId] = days;
    } else {
      delete this.trailDays[ship.massId];
    }

    if (isChecked) {
      const isSatMode = this.host.mapMode === 'sat';

      // sat Mode: Fetch trail data from aggregated satellite GPS endpoint
      if (isSatMode && ship.massSateliteId) {
        await this.fetchAndRenderSatTrail(ship, days, fitBounds);
        return;
      }

      const trailPayload = {
        massId: ship.massId,
        days,
        ...(this.host.options.trailRange || {}),
      };
      // Kunci rentang trail yang lagi kegambar. Start/end eksplisit dipakai apa
      // adanya; tanpa itu cukup `days`, soalnya rentangnya diturunin dari
      // lastGpsData yang gerak tiap pulse dan bakal bikin refetch terus.
      const rangeKey = trailPayload.start != null && trailPayload.end != null
        ? `${trailPayload.start}-${trailPayload.end}`
        : `d${days}`;
      const rangeChanged = this.trailRangeKey[ship.massId] !== rangeKey;
      // Cuma pemanggilan yang beneran narik jejak baru yang boleh geser peta;
      // refreshShips bisa jalan dua kali barengan dan yang satunya bawa data lama.
      // Render pertama dikecualiin biar deep link ?lat&lng&z gak ketimpa.
      const fitToNewTrail = rangeChanged && !this.firstRender;

      // Guard disableSummaryFetch sengaja gak dipakai di sini: trail sekarang
      // punya endpoint sendiri yang murah, dan fetchSummary gak lagi ngisi
      // koordinat — jadi semua halaman harus narik trailnya sendiri.
      //
      // Sengaja gak nengok coordinates.length: aset yang emang nol GPS di rentang
      // ini bakal balik kosong terus, sementara tiap fetch commit SET_ASSETS ->
      // watcher -> toggleTrail lagi. rangeKey nyatet "udah pernah ditarik buat
      // rentang ini", kosong atau enggak, jadi loopnya berhenti.
      if (forceRefetch || rangeChanged) {
        // Kunci dipasang SEBELUM await: refreshShips bisa manggil toggleTrail lagi
        // selagi fetch pertama masih jalan, dan kalo kuncinya nunggu await, dua-duanya
        // lolos dan nembak request yang sama persis. Gagal -> kunci dilepas biar bisa
        // dicoba lagi.
        this.notifyTrailState(ship.massId, 'loading');
        this.trailRangeKey[ship.massId] = rangeKey;
        this.trailFetching[ship.massId] = true;
        try {
          await this.host.fetchShipTrail(trailPayload);
          // Refresh ship reference to get updated data
          const updatedShip = this.findShip(ship.massId);
          if (updatedShip) {
            ship = updatedShip;
          }
        } catch (error) {
          delete this.trailRangeKey[ship.massId];
          console.error('[toggleTrail] Error fetching trail:', error?.message);
          this.pendingTrailFit = false;
          this.notifyTrailState(ship.massId, 'error');
          return;
        } finally {
          delete this.trailFetching[ship.massId];
        }
      } else if (this.trailFetching[ship.massId]) {
        // fetch pertama yang bakal gambar, jangan keburu lapor kosong
        return;
      }

      const finalCoordinates = ship.coordinates?.filter((x) => x[0] !== undefined && x[1] !== undefined) || [];

      if (finalCoordinates.length === 0) {
        console.warn('[toggleTrail] No coordinates available after fetching data');
        this.pendingTrailFit = false;
        this.notifyTrailState(ship.massId, 'empty');
        return;
      }

      // Create trail polyline with low zIndex to keep it below markers
      const trailColor = trackColor(ship.shipStatus);
      // Bersihin polyline lama dulu kalo masih ada (mis. refetch range tanpa toggle off duluan).
      if (this.trail[ship.massId]) {
        this.shipsLayerGroups[ship.massId].removeLayer(this.trail[ship.massId]);
        delete this.trail[ship.massId];
        delete this.trailSegments[ship.massId];
        this.toggleDotTrail(false, ship);
      }
      let trailLayer;
      if (this.host.options.trailColorBy === 'speed') {
        const points = (ship.coordinatesData || [])
          .filter((x) => x.latLong?.[0] !== undefined && x.latLong?.[1] !== undefined);
        const speedOf = (t) => (t.gpsData?.speed ?? t.speed);
        const thresholds = speedBuckets(points.map(speedOf));
        const colors = bucketColors(thresholds);
        const segments = [];
        for (let i = 1; i < points.length; i += 1) {
          const layer = L.polyline([points[i - 1].latLong, points[i].latLong], {
            color: colors[bucketOf(speedOf(points[i]), thresholds)],
            weight: 4,
          });
          segments.push({ layer, ts: Number(points[i].timestamp) * 1000 });
        }
        this.trailSegments[ship.massId] = segments;
        trailLayer = L.featureGroup(segments.map((s) => s.layer));
      } else {
        delete this.trailSegments[ship.massId];
        trailLayer = L.polyline(finalCoordinates, {
          color: trailColor,
          weight: 4,
          zIndexOffset: -1000, // Keep trail below all markers
        });
      }
      this.shipsLayerGroups[ship.massId].addLayer(trailLayer);
      this.trail[ship.massId] = trailLayer;
      if (fitBounds || fitToNewTrail) this.map.fitBounds(trailLayer.getBounds());
      this.toggleDotTrail(true, ship);
      this.anchorShipMarkers(ship);
      if (this.pendingTrailFit) this.fitToTrailOnce();
      this.notifyTrailState(ship.massId, 'ready');
    } else if (this.trail[ship.massId]) {
      this.shipsLayerGroups[ship.massId].removeLayer(this.trail[ship.massId]);
      delete this.trail[ship.massId];
      delete this.trailSegments[ship.massId];
      this.toggleDotTrail(false, ship);
      this.notifyTrailState(ship.massId, 'hidden');
    }
  }

  async fetchAndRenderSatTrail(ship, days = 1, fitBounds = true) {
    // Prefix `sat-` biar ganti mode standard <-> sat kebaca sebagai rentang beda.
    const rangeKey = `sat-d${days}`;
    // Kunci dipasang SEBELUM await, sama kayak jalur non-sat.
    this.trailRangeKey[ship.massId] = rangeKey;

    try {
      // Calculate time range (last N days, ngikut pilihan chip trail)
      const lastTimestamp = ship.lastGpsData?.timestamp || Math.floor(Date.now() / 1000);
      const startTimestamp = Math.floor(lastTimestamp / 3600) * 3600 - (days * 86400);
      const endTimestamp = Math.floor(Date.now() / 1000);

      // Fetch GPS, RPM, and AE data in parallel
      const [gpsResponse, rpmResponse, aeResponse] = await Promise.all([
        SeriesSvc.getAggregatedSatelliteGpsByPeriod(startTimestamp, endTimestamp, {
          params: {
            device: [ship.massSateliteId],
          },
        }),
        SeriesSvc.getAggregatedSatelliteRpmByPeriod(startTimestamp, endTimestamp, {
          params: {
            device: [ship.massSateliteId],
          },
        }),
        SeriesSvc.getAggregatedSatelliteAeByPeriod(startTimestamp, endTimestamp, {
          params: {
            device: [ship.massSateliteId],
          },
        }),
      ]);

      const gpsSeries = gpsResponse?.data?.data?.series || [];
      const rpmSeries = rpmResponse?.data?.data?.series || [];
      const aeSeries = aeResponse?.data?.data?.series || [];

      if (this.trailRangeKey[ship.massId] !== rangeKey) return;

      if (gpsSeries.length === 0) {
        return;
      }

      const mergedSeries = mergeSatSeries(gpsSeries, rpmSeries, aeSeries);

      // Extract coordinates from merged series
      const coordinates = mergedSeries.map((point) => [point.latitude, point.longitude]);

      // gambar ulang: garis & titik lama dibuang dulu biar gak numpuk
      const layerGroup = this.shipsLayerGroups[ship.massId];
      if (this.trail[ship.massId]) layerGroup.removeLayer(this.trail[ship.massId]);
      Object.values(this.dotTrail[ship.massId] || {}).forEach((marker) => layerGroup.removeLayer(marker));
      // titik standard sisa mode sebelumnya jangan dipakai sync dot & playback
      delete this.dotTrailPoints[ship.massId];
      this.removeCursor(ship.massId);

      // Create trail polyline with low zIndex and green color for sat
      const trailLayer = L.polyline(coordinates, {
        color: TRACK_COLORS.online,
        weight: 4,
        zIndexOffset: -1000, // Keep trail below all markers
      });
      this.shipsLayerGroups[ship.massId].addLayer(trailLayer);
      this.trail[ship.massId] = trailLayer;
      // refresh/pulse ngoper false biar peta gak loncat
      if (fitBounds) this.map.fitBounds(trailLayer.getBounds());

      // Create dot trail markers with popup data
      this.dotTrail[ship.massId] = [];
      const SatMarkerIcon = satDotIcon();

      // Skip the last (most recent) data point
      const seriesToRender = mergedSeries.slice(0, -1);
      seriesToRender.forEach((data) => {
        const marker = L.marker([data.latitude, data.longitude], {
          icon: SatMarkerIcon, // Always use online icon for sat dot trail
          rotationAngle: data.track || 0,
          rotationOrigin: '50% 50%',
          zIndexOffset: 1000, // Keep markers above trails
        });
        marker.bindPopup(() => createSatDotTrailPopupContent(ship, data));
        this.dotTrail[ship.massId].push(marker);
        this.shipsLayerGroups[ship.massId].addLayer(marker);
      });
      this.notifyActiveTrail();
    } catch (error) {
      if (this.trailRangeKey[ship.massId] === rangeKey) delete this.trailRangeKey[ship.massId];
      console.error('[fetchAndRenderSatTrail] Error fetching trail data:', error);
    }
  }

  createDotTrailMarker(ship, data, markerIcon) {
    const marker = L.marker(data.latLong, {
      icon: markerIcon,
      rotationAngle: data.heading || 0,
      rotationOrigin: '50% 50%',
      zIndexOffset: 1000,
    });
    marker.on('mouseover', () => publish(this.hoverChannel(), Number(data.timestamp) * 1000));
    marker.on('mouseout', () => publish(this.hoverChannel(), null));
    marker.bindPopup(() => this.createDotTrailPopupContent(ship, data));
    marker.on('popupclose', () => this.scheduleDotTrailSync());
    marker.on('popupopen', (e) => {
      this.currentDotTrailPopupPage = 1;
      const render = () => {
        const fresh = this.findShip(ship.massId) || ship;
        e.target.setPopupContent(this.createDotTrailPopupContent(fresh, data));
        this.setupDotTrailPaginationButtons(e.target, fresh, data);
      };
      render();

      // Detail RPM/AE/Total Cruise ditarik per titik, rentang satu jam.
      const shipNow = this.findShip(ship.massId) || ship;
      const cached = this.pointDetailOf(shipNow, data.timestamp);
      if (!cached && data.timestamp) {
        this.host.fetchTrailPointDetail({ massId: ship.massId, timestamp: data.timestamp })
          .then((detail) => {
            // simpan ke cache lokal, bukan Vuex, biar gak commit -> rebuild map -> popup nutup sendiri
            if (detail) {
              if (!this.pointDetail[ship.massId]) this.pointDetail[ship.massId] = {};
              this.pointDetail[ship.massId][data.timestamp] = detail;
            }
            render();
          })
          .catch((err) => console.warn('[dotTrail] detail points gagal difetch', err));
      }

      // Rollup udah bawa nama zona di lastPosition.zone.name, jadi
      // reverse-geocode cuma dipanggil kalau detailnya gagal/belum ada.
      const detailHasLocation = cached && cached.locationText;
      if (data.locationText === undefined && !detailHasLocation) {
        describeLocation(data.latLong[0], data.latLong[1]).then((text) => {
          data.locationText = text;
          if (this.currentDotTrailPopupPage === 1) render();
        });
      }
    });
    return marker;
  }

  toggleDotTrail(isChecked, ship) {
    if (isChecked && this.host.mapMode === 'sat' && ship.massSateliteId) {
      // titik sat statis, digambar ulang bareng garisnya
      this.fetchAndRenderSatTrail(ship, this.trailDays[ship.massId] || 1, false);
      return;
    }
    if (isChecked) {
      this.dotTrailPoints[ship.massId] = (ship.coordinatesData || [])
        .filter((x) => x.latLong[0] !== undefined && x.latLong[1] !== undefined);
      this.lastTrailMassId = String(ship.massId);
      this.syncDotTrail(ship);
    } else if (this.dotTrail[ship.massId]) {
      Object.values(this.dotTrail[ship.massId]).forEach((marker) => {
        this.shipsLayerGroups[ship.massId].removeLayer(marker);
      });
      delete this.dotTrail[ship.massId];
      delete this.dotTrailPoints[ship.massId];
      this.removeCursor(ship.massId);
    } else {
      delete this.dotTrailPoints[ship.massId];
      this.removeCursor(ship.massId);
    }
    if (!isChecked && this.lastTrailMassId === String(ship.massId)) this.lastTrailMassId = null;
    this.notifyActiveTrail();
  }

  firstActiveTrail() {
    const hasEnough = (id) => (this.dotTrailPoints[id] || []).length > 1;
    const massId = (this.lastTrailMassId && hasEnough(this.lastTrailMassId))
      ? this.lastTrailMassId
      : Object.keys(this.dotTrailPoints).find(hasEnough);
    if (!massId) return null;
    return { massId, points: this.dotTrailPoints[massId] };
  }

  notifyActiveTrail() {
    this.host.setActiveTrail(this.firstActiveTrail());
  }

  // Gambar cuma titik yang keliatan sekarang.
  syncDotTrail(ship) {
    const layerGroup = this.shipsLayerGroups[ship.massId];
    if (!this.dotTrailPoints[ship.massId] || !layerGroup || !this.map) return;
    const points = this.pointsUpToProgress(ship.massId);

    const b = this.map.getBounds();
    const wanted = visibleTrailPoints(points, {
      south: b.getSouth(),
      west: b.getWest(),
      north: b.getNorth(),
      east: b.getEast(),
    }, this.map.getZoom());

    if (!this.dotTrail[ship.massId]) this.dotTrail[ship.massId] = {};
    const markers = this.dotTrail[ship.massId];
    const keep = new Set(wanted.map((data) => String(data.timestamp)));

    Object.keys(markers).forEach((key) => {
      // popup kebuka jangan dicabut, dibersihin pas ditutup
      if (keep.has(key) || markers[key].isPopupOpen()) return;
      layerGroup.removeLayer(markers[key]);
      delete markers[key];
    });

    const markerIcon = this.host.mapMode === 'sat' ? satDotIcon() : ship.markerIcon;
    wanted.forEach((data) => {
      const key = String(data.timestamp);
      if (markers[key]) return;
      markers[key] = this.createDotTrailMarker(ship, data, markerIcon);
      layerGroup.addLayer(markers[key]);
    });
  }

  // Kanal hover bisa diganti halaman (voyage pakai hover:voyage).
  hoverChannel() {
    return this.host.options.hoverChannel || HOVER;
  }

  notifyTrailState(massId, state) {
    const handler = this.host.options.onTrailState;
    if (typeof handler === 'function') handler(massId, state);
  }

  // --- Marker rute A/B ---

  // Marker A/B asal-tujuan, cuma kalau halaman ngirim Options.routeMarkers.
  renderRouteMarkers() {
    const route = this.host.options.routeMarkers;
    if (!this.map || !route) return;
    if (this.routeMarkerLayer) {
      this.routeMarkerLayer.clearLayers();
    } else {
      this.routeMarkerLayer = L.layerGroup().addTo(this.map);
    }
    routeMarkerPoints(route.origin, route.destination).forEach((point) => {
      const icon = L.divIcon({
        className: `route-marker route-marker-${point.key.toLowerCase()}`,
        html: `<div class="route-marker-badge">${point.key}</div>`,
        iconSize: [24, 40],
        iconAnchor: [12, 40],
        popupAnchor: [0, -40],
      });
      const title = point.key === 'A' ? 'Origin' : 'Destination';
      // pin di atas titik & di atas ikon kapal biar B gak ketutup
      L.marker([point.lat, point.lng], { icon, zIndexOffset: 2200, riseOnHover: true })
        .bindPopup(`<b>${title}</b><br>${escapeHtml(point.name)}`)
        .addTo(this.routeMarkerLayer);
    });
  }

  // Dipakai kalau trail gak ada biar peta gak diem di view default.
  fitToRouteMarkers() {
    const route = this.host.options.routeMarkers;
    if (!this.map || !route) return;
    const points = routeMarkerPoints(route.origin, route.destination);
    if (points.length === 1) {
      this.map.setView([points[0].lat, points[0].lng], 8);
    } else if (points.length === 2) {
      this.map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [50, 50] });
    }
  }

  // --- Kursor & playback ---

  // Penanda waktu di trail, digerakin crosshair chart lewat kanal HOVER.
  moveCursor(ms) {
    if (!this.map) return;
    Object.keys(this.dotTrailPoints).forEach((massId) => {
      const points = ms === null || ms === undefined
        ? null
        : nearestTrailPoint(this.dotTrailPoints[massId], ms);
      if (!points) {
        this.removeCursor(massId);
        return;
      }
      const content = this.cursorLabel(points);
      if (this.cursorMarkers[massId]) {
        this.cursorMarkers[massId].setLatLng(points.latLong).setTooltipContent(content);
        return;
      }
      this.cursorMarkers[massId] = L.circleMarker(points.latLong, {
        radius: 9,
        color: '#ff69b4',
        weight: 3,
        fillColor: '#ff69b4',
        fillOpacity: 0.25,
        interactive: false,
      })
        .bindTooltip(content, { permanent: true, direction: 'top', offset: [0, -10] })
        .addTo(this.map);
    });
  }

  removeCursor(massId) {
    const marker = this.cursorMarkers[massId];
    if (!marker) return;
    if (this.map) this.map.removeLayer(marker);
    delete this.cursorMarkers[massId];
  }

  // Geser ikon kapal & potong jejaknya ke posisi waktu itu.
  playbackAt(massId, ms) {
    const points = this.dotTrailPoints[massId];
    if (!this.map || !points) return null;
    const pos = positionAt(points, ms);
    if (!pos) return null;
    this.playbackState = { massId, ms };

    this.moveShipMarkers(massId, L.latLng(pos.lat, pos.lng), pos.heading);
    this.setTrailProgress(massId, ms);
    return pos;
  }

  // Yang muter cuma penunjuk arah. Kalau ikonnya gak punya penunjuk terpisah
  // (gaya simple), badan kapalnya sendiri yang jadi penunjuk.
  moveShipMarkers(massId, latLng, angle) {
    const body = this.shipMarkers[massId];
    const arrow = this.shipHeadingMarkers[massId];
    [body, arrow].forEach((m) => { if (m) m.setLatLng(latLng); });
    const rotator = arrow || body;
    if (rotator && typeof rotator.setRotationAngle === 'function') rotator.setRotationAngle(angle);
  }

  // Titik istirahat ikon: ujung trail kalau rentangnya dikunci, kalau enggak posisi live.
  shipAnchor(ship) {
    const points = this.dotTrailPoints[ship.massId];
    if (this.host.options.trailRange && points && points.length) {
      const last = points[points.length - 1];
      return {
        latLng: L.latLng(last.latLong[0], last.latLong[1]),
        angle: Number(last.heading) || 0,
      };
    }
    return {
      latLng: ship.lastLatLng,
      angle: Number(ship.lastGpsData && ship.lastGpsData.track) || 0,
    };
  }

  anchorShipMarkers(ship) {
    const { latLng, angle } = this.shipAnchor(ship);
    if (!latLng) return;
    this.moveShipMarkers(ship.massId, latLng, angle);
  }

  // ms null = tampilkan utuh.
  setTrailProgress(massId, ms) {
    this.setLineProgress(massId, ms);
    const ship = this.findShip(massId);
    if (ship) this.syncDotTrail(ship);
  }

  setLineProgress(massId, ms) {
    const segments = this.trailSegments[massId];
    const group = this.trail[massId];
    if (segments && group) {
      segments.forEach((s) => {
        const visible = ms === null || s.ts <= ms;
        if (visible && !group.hasLayer(s.layer)) group.addLayer(s.layer);
        if (!visible && group.hasLayer(s.layer)) group.removeLayer(s.layer);
      });
      return;
    }
    if (!group || typeof group.setLatLngs !== 'function') return;
    if (ms === null) {
      const ship = this.findShip(massId);
      if (ship && ship.coordinates) group.setLatLngs(ship.coordinates);
      return;
    }
    group.setLatLngs(pathUpTo(this.dotTrailPoints[massId] || [], ms));
  }

  // Selagi replay jalan, titik yang waktunya belum kelewat belum digambar.
  pointsUpToProgress(massId) {
    const points = this.dotTrailPoints[massId] || [];
    const st = this.playbackState;
    if (!st || String(st.massId) !== String(massId)) return points;
    return points.filter((p) => Number(p.timestamp) * 1000 <= st.ms);
  }

  // Balikin ikon ke titik istirahatnya dan jejaknya ke utuh.
  playbackEnd(massId) {
    this.playbackState = null;
    const ship = this.findShip(massId);
    if (!ship) return;

    this.anchorShipMarkers(ship);
    this.setTrailProgress(massId, null);
  }

  cursorLabel(points) {
    const hourLabel = formatDateTime(Number(points.timestamp) * 1000, 'DD MMM HH:mm');
    const knot = kmhToKnots(points.gpsData && points.gpsData.speed);
    return knot === null ? hourLabel : `${hourLabel} · ${knot.toFixed(1)} kn`;
  }

  scheduleDotTrailSync() {
    if (this.dotTrailSyncTimeout) clearTimeout(this.dotTrailSyncTimeout);
    this.dotTrailSyncTimeout = setTimeout(() => {
      Object.keys(this.dotTrailPoints).forEach((massId) => {
        const ship = this.findShip(massId);
        if (ship) this.syncDotTrail(ship);
      });
    }, 150);
  }

  // --- Logika Popup ---

  // Listener-nya didelegasikan sekali di setupPopupDelegation(), jadi cukup ganti isi.
  updatePopupContent(popup, ship) {
    popup.setContent(this.createPopupContent(ship));
  }

  // Delegasi satu kali di container peta. Tahan terhadap berapa kali pun Leaflet
  // bikin ulang isi popup, soalnya yang didengerin containernya, bukan node-nya.
  setupPopupDelegation(map) {
    map.getContainer().addEventListener('change', (event) => {
      const el = event.target;
      const id = (el && el.id) || '';
      const trail = id.match(/^trail-switch-(\d+)$/);
      const dot = id.match(/^dot-trail-switch-(\d+)$/);
      if (!trail && !dot) return;

      const massId = (trail || dot)[1];
      const ship = this.findShip(massId);
      if (!ship) return;

      if (dot) {
        this.toggleDotTrail(el.checked, ship);
        return;
      }

      const isChecked = el.checked;
      (async () => {
        if (isChecked) await this.host.getLastData(this.host.mapMode);
        // Nyalain = paksa refetch biar window ngikut chip yg dipilih (mirror sidebar).
        await this.toggleTrail(isChecked, ship, this.trailDays[ship.massId] || 1, isChecked === true);
        this.refreshOpenPopup(ship.massId);
      })();
    });

    // Chip rentang trail (1/3/7/14 hari).
    map.getContainer().addEventListener('click', (event) => {
      const id = (event.target && event.target.id) || '';
      const hit = id.match(/^trail-day-(\d+)-(\d+)$/);
      if (!hit) return;
      const massId = hit[1];
      const days = Number(hit[2]);
      if (!TRAIL_DAY_OPTIONS.includes(days)) return;
      const ship = this.findShip(massId);
      if (!ship || (this.trailDays[massId] || 1) === days) return;

      this.trailDays[massId] = days;
      (async () => {
        if (this.trailCheckboxStatus[massId] === true) {
          await this.toggleTrail(true, ship, days, true);
        }
        this.refreshOpenPopup(massId);
      })();
    });

    // Tombol aksi & pager popup. Anchored biar pager dot-trail (id-nya berakhiran timestamp) gak ikut kesenggol.
    map.getContainer().addEventListener('click', (event) => {
      const button = event.target.closest('button');
      const id = (button && button.id) || '';

      const report = id.match(/^report-(\d+)$/);
      if (report) {
        this.host.clickReport(Number(report[1]));
        return;
      }
      const dashboard = id.match(/^dashboard-(\d+)$/);
      if (dashboard) {
        this.host.clickDashboard(Number(dashboard[1]));
        return;
      }
      const voyage = id.match(/^voyage-(\d+)$/);
      if (voyage) {
        this.host.clickVoyage(Number(voyage[1]));
        return;
      }
      const prev = id.match(/^previous-popup-button-(\d+)$/);
      const next = id.match(/^next-popup-button-(\d+)$/);
      if (!prev && !next) return;

      const massId = (prev || next)[1];
      const ship = this.findShip(massId);
      const popup = this.findOpenPopup(massId);
      if (!ship || !popup) return;

      const total = this.totalPopupPages || 1;
      if (total < 2) return;
      this.currentPopupPage = prev
        ? ((this.currentPopupPage - 2 + total) % total) + 1
        : (this.currentPopupPage % total) + 1;
      this.updatePopupContent(popup, ship);
    });
  }

  // Cari popup kapal yang lagi kebuka, lalu render ulang isinya.
  refreshOpenPopup(massId) {
    if (!this.map || this.openPopupMassId !== massId) return;
    const popup = this.findOpenPopup(massId);
    const ship = this.findShip(massId);
    if (popup && ship) this.updatePopupContent(popup, ship);
  }

  // Popup kapal ini yang lagi kebuka di peta, atau undefined.
  findOpenPopup(massId) {
    const layers = this.shipsLayerGroups[massId] && this.shipsLayerGroups[massId].getLayers();
    return (layers || [])
      .map((l) => l.getPopup && l.getPopup())
      .find((p) => p && this.map.hasLayer(p));
  }

  // Cache lokal dulu; trailPointDetail store cuma diisi fetchSummary jalur lama.
  pointDetailOf(ship, timestamp) {
    return this.pointDetail[ship.massId]?.[timestamp] || ship.trailPointDetail?.[timestamp];
  }

  setupButtonEvent(buttonId, callback) {
    setTimeout(() => {
      const button = document.getElementById(buttonId);
      if (button) {
        button.onclick = callback;
      }
    }, 0);
  }

  get isCompactPopup() {
    const opt = this.host.options;
    return opt.compactPopup === true || opt.compactPopup === 'true';
  }

  // Trail stats ditarik pas user minta detail (buka popup / expand panel),
  // bukan pas gambar garis. Dashboard & Report udah punya summary dari
  // halamannya, jadi kelewat di sini.
  async ensureShipSummary(ship) {
    if (!ship) return;
    // Popup sat pakai createSatPopupContent, stats-nya gak kepake sama sekali.
    if (this.host.mapMode === 'sat') return;
    if (this.host.options.disableSummaryFetch === true) return;
    // Gak ada cek rangeKey di sini — action fetchShipSummary yang pegang guard
    // itu, biar logika rentangnya cuma hidup di satu tempat.

    try {
      await this.host.fetchShipSummary({
        massId: ship.massId,
        days: this.trailDays[ship.massId] || 1,
        ...(this.host.options.trailRange || {}),
      });
    } catch (e) {
      console.warn('[ensureShipSummary] gagal, popup tetap visible tanpa stats', e);
      return;
    }

    const updated = this.findShip(ship.massId);
    const popup = this.map && this.openPopupMassId === ship.massId
      ? this.findOpenPopup(ship.massId)
      : null;
    if (updated && popup) this.updatePopupContent(popup, updated);
  }

  createPopupContent(ship) {
    const isSatMode = this.host.mapMode === 'sat';

    if (isSatMode) {
      // mode sat punya popup sendiri
      return this.createSatPopupContent(ship);
    }

    // Standard mode: pager device-aware (mirror sidebar lewat util vesselDetail)
    const userAccess = this.host.store?.getters['auth/userAccess'];
    const groups = buildDetailGroups(ship, userAccess);
    this.totalPopupPages = groups.length || 1;
    if (this.currentPopupPage > this.totalPopupPages || this.currentPopupPage < 1) {
      this.currentPopupPage = 1;
    }
    const activeGroup = groups[this.currentPopupPage - 1] || groups[0];
    const lastData = ship.lastGpsData?.timestamp
      ? formatDateTime(ship.lastGpsData.timestamp * 1000, DISPLAY_DATETIME)
      : '-';

    return /* html */`
      <div class="row pt-3">
        <div class="col-7 d-flex align-items-center"><h5 style="margin: 0;">${ship.massName || '-'}</h5></div>
        <div class="col-5 d-flex justify-content-end align-items-center">${renderStatusBadge({ color: shipStatusColor(ship), label: shipStatusLabel(ship) })}</div>
      </div>
      <hr style="margin: 10px 0;" />
      <div class="row"><div class="col-12"><p style="margin: 0; font-size: 13px"><b>Last Data:</b> ${lastData}</p></div></div>
      <hr style="margin: 10px 0;" />
      ${renderDetailPager(ship, groups, this.currentPopupPage)}
      ${renderGroupBody(activeGroup)}
      ${this.isCompactPopup ? '' : /* html */`
      <hr style="margin: 10px 0;" />
      <div class="row pb-2">${this.renderTrailPanelFull(ship)}</div>
      <hr style="margin: 10px 0;" />
      <div class="row button-section">${this.renderActionButtons(ship)}</div>`}
    `;
  }

  createSatPopupContent(ship) {
    const gpsData = ship.lastGpsData;
    const latitude = gpsData?.latitude ?? gpsData?.lat;
    const longitude = gpsData?.longitude ?? gpsData?.lng;
    const timestamp = gpsData?.timestamp;
    const datetime = timestamp ? formatDateTime(timestamp * 1000, DISPLAY_DATETIME) : '-';

    const speedValue = gpsData?.speed ?? null; // backend provides speed in KNOT
    const headingValue = gpsData?.track ?? gpsData?.heading ?? null;
    const headingDisplay = Number.isFinite(Number(headingValue)) ? `${formatNum(Number(headingValue), 2)}°` : '-';

    // Convert decimal coordinates to DMS
    const latDMS = latitude ? convertDecimalToDMS(latitude, true) : '-';
    const lngDMS = longitude ? convertDecimalToDMS(longitude, false) : '-';
    const coordinateDisplay = latitude && longitude ? `${latDMS}, ${lngDMS}` : '-';

    // Get RPM data - extract unique types from lastRpmData
    const rpmTypes = [...new Set((ship.lastRpmData || []).map((rpm) => rpm.type))];
    const displayLabels = rpmTypes.map((type) => getRpmTypeLabel(type, rpmTypes));

    // Build RPM HTML dynamically based on available types
    let rpmHtml = '';
    rpmTypes.forEach((type, index) => {
      const rpmData = ship.lastRpmData?.find((rpm) => rpm.type === type);
      const rpmValue = Math.floor(rpmData?.rpm || 0);
      const displayLabel = displayLabels[index];
      rpmHtml += `
        <div class="col-6" style="height: 60px"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">${displayLabel} RPM</p><p style="font-weight: 1000; color: #1e1e1e">${rpmValue}</p></div>
      `;
    });

    // Get AE data
    const activeAe = ship.lastAeData?.find((ae) => ae.status === 1) || ship.lastAeData?.[0];
    const currentAe = activeAe?.no;

    const infoHTML = `
      <div class="col-6" style="height: 60px"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Coordinates</p><p style="font-weight: 1000; color: #1e1e1e">${coordinateDisplay}</p></div>
      <div class="col-6" style="height: 60px"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Speed</p><p style="font-weight: 1000; color: #1e1e1e">${speedValue !== null ? `${formatNum(speedValue, 2)} knot` : '-'}</p></div>
      <div class="col-6" style="height: 60px"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Heading</p><p style="font-weight: 1000; color: #1e1e1e">${headingDisplay}</p></div>
      ${rpmHtml}
      ${currentAe !== null && currentAe !== undefined ? `
      <div class="col-6" style="height: 60px"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Active AE</p><p style="font-weight: 1000; color: #1e1e1e">AE ${currentAe}</p></div>
      ` : ''}
    `;

    const statusClass = ship.shipStatus || 'online';

    return /* html */`
      <div class="row pt-3">
        <div class="col-6 d-flex align-items-center"><h5>${ship.massName || '-'}</h5></div>
        <div class="col-6 d-flex justify-content-end align-items-center pt-0"><h5 class="status ${statusClass}">${statusClass}</h5></div>
      </div>
      <hr style="margin: 12px 0;" />
      <div class="row"><div class="col-12"><p style="margin: 0; font-size: 14px"><b>Last Data: </b> ${datetime}</p></div></div>
      <hr style="margin: 12px 0;" />
      <div class="row info-section pb-2">${infoHTML}</div>
      <hr style="margin: 12px 0;" />
      <div class="row pb-3">${this.renderSatTrailSwitches(ship)}</div>
      <hr style="margin: 12px 0;" />
      <div class="row button-section">${this.renderActionButtons(ship)}</div>
    `;
  }

  renderTrailPanelFull(ship) {
    const isChecked = this.trailCheckboxStatus[ship.massId] === true;
    const hasDotTrail = !!this.dotTrailPoints[ship.massId]
      || Object.keys(this.dotTrail[ship.massId] || {}).length > 0;
    const activeDays = this.trailDays[ship.massId] || 1;

    // Halaman yang ngunci rentang lewat Options.trailRange gak nampilin chip:
    // start/end eksplisit selalu menang atas `days`, jadi chip-nya gak ngefek.
    const lockedRange = this.host.options.trailRange;
    const chips = lockedRange ? '' : TRAIL_DAY_OPTIONS.map((d) => {
      const style = activeDays === d
        ? 'background:#264284;border:1px solid #264284;color:#fff;'
        : 'background:#fff;border:1px solid #e6eaf1;color:#6b7280;';
      return `<button type="button" class="trail-day-chip" id="trail-day-${ship.massId}-${d}" style="${style}padding:1px 7px;border-radius:6px;font-size:11px;font-weight:600;margin-left:4px;cursor:pointer;">${d}d</button>`;
    }).join('');

    const stats = buildTrailStats(ship);
    let body;
    if (isChecked && stats.length) {
      const cells = stats.map((s) => `
        <div style="margin-bottom:4px;">
          <div style="font-size:10px;color:#8a93a5;text-transform:uppercase;">${s.label}</div>
          <div style="font-size:13px;font-weight:700;color:#1f2937;">${s.value}</div>
        </div>`).join('');
      body = `<div style="display:grid;grid-template-columns:1fr 1fr;gap:2px 12px;margin-top:8px;padding:8px;border:1px solid #e6eaf1;border-radius:8px;background:#fafbfc;">${cells}</div>`;
    } else {
      const hint = isChecked
        ? `No trail data for the last ${activeDays}-day window.`
        : `Enable Vessel Trail to see the last ${activeDays}-day summary.`;
      body = `<div style="margin-top:8px;font-size:11px;font-style:italic;color:#8a93a5;">${hint}</div>`;
    }

    return /* html */`
      <div class="col-12">
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="trail-switch-${ship.massId}" ${isChecked ? 'checked' : ''} />
            <label class="custom-control-label" for="trail-switch-${ship.massId}">Vessel Trail</label>
          </div>
          <div>${chips}</div>
        </div>
        ${body}
        ${isChecked ? `
        <div class="custom-control custom-switch" style="margin-top:8px;">
          <input type="checkbox" class="custom-control-input" id="dot-trail-switch-${ship.massId}" ${hasDotTrail ? 'checked' : ''} />
          <label class="custom-control-label" for="dot-trail-switch-${ship.massId}">Dot Trail</label>
        </div>` : ''}
      </div>
    `;
  }

  renderSatTrailSwitches(ship) {
    const isChecked = this.trailCheckboxStatus[ship.massId];
    const hasDotTrail = !!this.dotTrailPoints[ship.massId]
      || Object.keys(this.dotTrail[ship.massId] || {}).length > 0;

    return /* html */`
        <div class="col-12 d-flex justify-content-between align-items-center">
          <div>
            <div class="custom-control custom-switch mr-2">
              <input type="checkbox" class="custom-control-input" id="trail-switch-${ship.massId}" ${isChecked ? 'checked' : ''} />
              <label class="custom-control-label" for="trail-switch-${ship.massId}">Vessel Trail</label>
            </div>
            ${isChecked ? `
            <div class="custom-control custom-switch mr-2">
              <input type="checkbox" class="custom-control-input" id="dot-trail-switch-${ship.massId}" ${hasDotTrail ? 'checked' : ''} />
              <label class="custom-control-label" for="dot-trail-switch-${ship.massId}">Dot Trail</label>
            </div>` : ''}
          </div>
          <button class="btn btn-sm btn-mantraz" style="display: flex; align-items: center; justify-content: center;" onclick="window.mapServiceVm.viewSatelliteDatalog('${ship.massId}')">
            <i class="fas fa-file-alt mr-2"></i>View Log
          </button>
        </div>
      `;
  }

  renderActionButtons(ship) {
    const userAccess = this.host.store?.getters['auth/userAccess'] || {};
    let content = '';
    if (userAccess.summary_aggregated?.includes('read')) {
      content += /* html */`
        <div class="col-6 my-1"><button class="btn btn-sm btn-block btn-mantraz tooltip-button" id="report-${ship.massId}">View Report</button></div>
        <div class="col-6 my-1"><button class="btn btn-sm btn-block btn-outline-mantraz tooltip-button" id="dashboard-${ship.massId}">View Dashboard</button></div>
      `;
    }
    const hideVoyageButton = this.host.options.hideVoyageButton === true || this.host.options.hideVoyageButton === 'true';
    if (!hideVoyageButton && userAccess.voyage_master?.includes('read')) {
      content += `<div class="col-12 my-1"><button class="btn btn-sm btn-block btn-mantraz tooltip-button" id="voyage-${ship.massId}">Voyage Report</button></div>`;
    }
    return content;
  }

  // --- Dot Trail Popup ---

  createDotTrailPopupContent(ship, data) {
    const detail = this.pointDetailOf(ship, data.timestamp);
    const groups = buildDotTrailGroups(data, detail, {
      wave: this.host.options.dotPopupWave,
      rpmLabel: getRpmTypeLabel,
    });
    if (this.currentDotTrailPopupPage > groups.length || this.currentDotTrailPopupPage < 1) {
      this.currentDotTrailPopupPage = 1;
    }
    const activeGroup = groups[this.currentDotTrailPopupPage - 1] || groups[0];
    const suffix = `-${data.timestamp}`;
    const lastData = data.gpsData?.timestamp
      ? formatDateTime(data.gpsData.timestamp * 1000, DISPLAY_DATETIME)
      : '-';
    const rangeLabel = hourWindowLabel(data.timestamp);

    return /* html */`
      <div class="row pt-3">
        <div class="col-7 d-flex align-items-center"><h5 style="margin: 0;">${ship.massName || '-'}</h5></div>
        <div class="col-5 d-flex justify-content-end align-items-center">${renderStatusBadge({ color: shipStatusColor(ship), label: shipStatusLabel(ship) })}</div>
      </div>
      <hr style="margin: 10px 0;" />
      <div class="row"><div class="col-12"><p style="margin: 0; font-size: 13px"><b>Last Data:</b> ${lastData}</p></div></div>
      ${rangeLabel ? `<div class="row"><div class="col-12"><p style="margin: 0; font-size: 13px"><b>Data Range:</b> ${rangeLabel}</p></div></div>` : ''}
      <hr style="margin: 10px 0;" />
      ${renderDetailPager(ship, groups, this.currentDotTrailPopupPage, suffix)}
      ${renderGroupBody(activeGroup)}
    `;
  }

  setupDotTrailPaginationButtons(marker, ship, data) {
    // detail perlu ikut dibaca, soalnya RPM group cuma muncul kalau detail-nya ada
    const detail = this.pointDetailOf(ship, data.timestamp);
    const total = buildDotTrailGroups(data, detail, {
      wave: this.host.options.dotPopupWave,
      rpmLabel: getRpmTypeLabel,
    }).length;
    const go = (step) => {
      if (total < 2) return;
      this.currentDotTrailPopupPage = ((this.currentDotTrailPopupPage - 1 + step + total) % total) + 1;
      marker.setPopupContent(this.createDotTrailPopupContent(ship, data));
      this.setupDotTrailPaginationButtons(marker, ship, data);
    };
    this.setupButtonEvent(`previous-popup-button-${ship.massId}-${data.timestamp}`, () => go(-1));
    this.setupButtonEvent(`next-popup-button-${ship.massId}-${data.timestamp}`, () => go(1));
  }

  // --- Fokus kapal dari sidebar ---

  // Zoom 12: citra satelit laut lepas habis di atas itu dan ubinnya balik hitam.
  panToShip(ship) {
    const { latLng } = this.shipAnchor(ship);
    if (!this.map || !latLng) return;

    this.map.setView(latLng, SHIP_FOCUS_ZOOM, {
      animate: true,
      pan: {
        duration: 1,
      },
    });
  }

  // Denyut marker pas barisnya disorot di sidebar.
  highlightShip(massId) {
    const previous = this.shipMarkers[this.highlightedMassId];
    const prevElement = previous && previous.getElement();
    if (prevElement) prevElement.classList.remove('ship-highlight');
    this.highlightedMassId = null;

    if (massId === null || massId === undefined) return;
    const marker = this.shipMarkers[massId];
    const element = marker && marker.getElement();
    if (!element) return;
    element.classList.add('ship-highlight');
    this.highlightedMassId = massId;
  }

  // --- Kontrol pengaturan peta ---

  addMapSettingsControl() {
    if (!this.map) return;
    const MapSettingsControl = L.Control.extend({
      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-map-settings');
        const toggle = L.DomUtil.create('button', 'map-settings-toggle', container);
        toggle.type = 'button';
        toggle.innerHTML = '<i class="ri-settings-3-line"></i>';

        const panel = L.DomUtil.create('div', 'map-settings-panel hidden', container);
        panel.innerHTML = `
          <div class="map-settings-item map-settings-header">Base Map</div>
          <label class="map-settings-option">
            <input type="radio" name="map-base-layer" value="satellite" />
            <span>Satellite</span>
          </label>
          <label class="map-settings-option">
            <input type="radio" name="map-base-layer" value="vectorColor" />
            <span>Vector color</span>
          </label>
          <label class="map-settings-option">
            <input type="radio" name="map-base-layer" value="vectorWhite" />
            <span>Vector white</span>
          </label>
          <div class="map-settings-separator"></div>
          <div class="map-settings-item map-settings-header">Ship Icon</div>
          <label class="map-settings-option">
            <input type="radio" name="map-icon-style" value="special" />
            <span>Detailed</span>
            <img class="map-icon-sample" src="${detailedIconSample}" alt="" />
          </label>
          <label class="map-settings-option">
            <input type="radio" name="map-icon-style" value="simple" />
            <span>Simple</span>
            <img class="map-icon-sample" src="${simpleIconSample}" alt="" />
          </label>
          <div class="map-settings-separator"></div>
          <div class="map-settings-item map-settings-header">Icon Size</div>
          <div class="map-settings-size">
            <input type="range" id="map-icon-scale" min="0.6" max="1.8" step="0.1" />
            <span id="map-icon-scale-value" class="map-settings-size-value">100%</span>
          </div>
          <div class="map-settings-separator"></div>
          <label class="map-settings-switch">
            <input type="checkbox" id="map-toggle-landmarks" />
            <span>Landmarks</span>
          </label>
          <label class="map-settings-switch">
            <input type="checkbox" id="map-toggle-timezones" />
            <span>Timezone</span>
          </label>
        `;

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        toggle.onclick = () => {
          panel.classList.toggle('hidden');
        };

        const baseInputs = panel.querySelectorAll('input[name="map-base-layer"]');
        baseInputs.forEach((input) => {
          input.addEventListener('change', (event) => {
            this.setBaseLayer(event.target.value);
          });
        });

        const iconStyleInputs = panel.querySelectorAll('input[name="map-icon-style"]');
        iconStyleInputs.forEach((input) => {
          input.addEventListener('change', (event) => {
            this.setIconStyle(event.target.value);
          });
        });

        const scaleInput = panel.querySelector('#map-icon-scale');
        const scaleValue = panel.querySelector('#map-icon-scale-value');
        if (scaleInput) {
          // Update the label live while dragging, but only re-render on release.
          scaleInput.addEventListener('input', (event) => {
            if (scaleValue) {
              scaleValue.textContent = `${Math.round(parseFloat(event.target.value) * 100)}%`;
            }
          });
          scaleInput.addEventListener('change', (event) => {
            this.setIconScale(parseFloat(event.target.value));
          });
        }

        const landmarksToggle = panel.querySelector('#map-toggle-landmarks');
        landmarksToggle.addEventListener('change', (event) => {
          this.toggleLandmarks(event.target.checked);
        });

        const timezonesToggle = panel.querySelector('#map-toggle-timezones');
        timezonesToggle.addEventListener('change', (event) => {
          this.toggleTimezones(event.target.checked);
        });

        this.mapSettingsPanel = panel;
        this.syncMapSettingsUI();

        return container;
      },
    });
    this.map.addControl(new MapSettingsControl({ position: 'topleft' }));
  }

  addFullscreenControl() {
    if (!this.map || !document.documentElement.requestFullscreen) return;
    const FullscreenControl = L.Control.extend({
      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-map-settings');
        const button = L.DomUtil.create('button', 'map-settings-toggle', container);
        button.type = 'button';
        button.title = 'Toggle fullscreen';
        button.innerHTML = '<i class="ri-fullscreen-line"></i>';
        L.DomEvent.disableClickPropagation(container);

        button.onclick = () => {
          if (document.fullscreenElement) document.exitFullscreen();
          else this.map.getContainer().requestFullscreen();
        };

        this.fullscreenHandler = () => {
          button.innerHTML = document.fullscreenElement
            ? '<i class="ri-fullscreen-exit-line"></i>'
            : '<i class="ri-fullscreen-line"></i>';
        };
        document.addEventListener('fullscreenchange', this.fullscreenHandler);
        return container;
      },
    });
    this.map.addControl(new FullscreenControl({ position: 'topleft' }));
  }

  syncMapSettingsUI() {
    if (!this.mapSettingsPanel) return;
    const activeBaseInput = this.mapSettingsPanel.querySelector(`input[name="map-base-layer"][value="${this.activeBaseLayerKey}"]`);
    if (activeBaseInput) {
      activeBaseInput.checked = true;
    }
    const activeIconStyleInput = this.mapSettingsPanel.querySelector(`input[name="map-icon-style"][value="${getMapIconStyle()}"]`);
    if (activeIconStyleInput) {
      activeIconStyleInput.checked = true;
    }
    const scaleInput = this.mapSettingsPanel.querySelector('#map-icon-scale');
    if (scaleInput) {
      const currentScale = getMapIconScale();
      scaleInput.value = currentScale;
      const scaleValue = this.mapSettingsPanel.querySelector('#map-icon-scale-value');
      if (scaleValue) {
        scaleValue.textContent = `${Math.round(currentScale * 100)}%`;
      }
    }
    const landmarksToggle = this.mapSettingsPanel.querySelector('#map-toggle-landmarks');
    if (landmarksToggle) {
      landmarksToggle.checked = Boolean(this.landmark && this.landmark.visible);
    }
    const timezonesToggle = this.mapSettingsPanel.querySelector('#map-toggle-timezones');
    if (timezonesToggle) {
      timezonesToggle.checked = Boolean(this.timezone && this.timezone.visible);
    }
  }

  setBaseLayer(layerKey) {
    if (!this.map || !this.baseLayers[layerKey]) return;
    if (this.activeBaseLayer) {
      this.map.removeLayer(this.activeBaseLayer);
    }
    this.activeBaseLayer = this.baseLayers[layerKey];
    this.activeBaseLayerKey = layerKey;
    this.activeBaseLayer.addTo(this.map);
    localStorage.setItem('activeBaseLayer', layerKey);
    this.syncMapSettingsUI();
  }

  setIconStyle(style) {
    this.rebuildShipIcons(persistMapIconStyle(style));
  }

  setIconScale(scale) {
    persistMapIconScale(scale);
    this.rebuildShipIcons();
  }

  // style undefined = buildIconConfig pakai preferensi tersimpan.
  rebuildShipIcons(style) {
    // Kapal peta ini belum tentu dari store (mis. peta FMS), jadi dihitung di sini juga.
    this.host.ships.forEach((ship) => {
      if (ship.shipStatus) {
        const { iconConfig, markerIconUrl } = buildIconConfig(ship, ship.shipStatus, style);
        ship.iconConfig = iconConfig;
        ship.markerIconUrl = markerIconUrl;
      }
      ship.icon = null;
      ship.markerIcon = null;
      ship.headingIcon = null;
    });

    if (this.host.store) {
      this.host.store.dispatch('asset/applyIconSettings');
    }

    this.refreshShips();
    this.syncMapSettingsUI();
  }

  toggleLandmarks(forceState) {
    if (this.landmark) this.landmark.toggle(forceState);
  }

  toggleTimezones(forceState) {
    return this.timezone ? this.timezone.toggle(forceState) : undefined;
  }

  handleLandmarkViewportChange() {
    if (this.landmark) this.landmark.handleViewportChange();
  }

  // --- Cluster kapal ---

  // Kapal yang berdempetan di layar digabung jadi satu lencana.
  syncShipClusters() {
    if (!this.map) return;
    if (!this.shipClusterLayer) this.shipClusterLayer = L.layerGroup().addTo(this.map);
    this.shipClusterLayer.clearLayers();

    // Di zoom maksimal klik lencana gak bisa zoom lagi, jadi jangan digabung.
    const atMaxZoom = this.map.getZoom() >= this.map.getMaxZoom();
    const candidate = [];
    (this.host.ships || []).forEach((ship) => {
      if (ship.isVisible === false || !ship.lastLatLng || !this.shipsLayerGroups[ship.massId]) return;
      // trail nempel di layer group kapal; kalau ikut digabung garisnya ilang
      if (atMaxZoom || this.trailCheckboxStatus[ship.massId]) {
        this.shipsLayerGroups[ship.massId].addTo(this.map);
      } else {
        candidate.push(ship);
      }
    });

    const clusters = clusterByGrid(candidate, this.shipClusterPx, (ship) => {
      const points = this.map.latLngToContainerPoint(ship.lastLatLng);
      return [points.y, points.x];
    });

    clusters.forEach((cluster) => {
      if (cluster.items.length === 1) {
        this.shipsLayerGroups[cluster.items[0].massId].addTo(this.map);
        return;
      }
      cluster.items.forEach((ship) => {
        this.map.removeLayer(this.shipsLayerGroups[ship.massId]);
      });
      const center = this.map.containerPointToLatLng(L.point(cluster.lng, cluster.lat));
      const marker = L.marker(center, {
        icon: this.createShipClusterIcon(cluster.items),
        zIndexOffset: 2200,
      });
      marker.on('click', () => {
        this.map.fitBounds(
          L.latLngBounds(cluster.items.map((ship) => ship.lastLatLng)),
          { padding: [80, 80] },
        );
      });
      marker.addTo(this.shipClusterLayer);
    });
  }

  scheduleShipClusterSync() {
    if (this.shipClusterTimeout) clearTimeout(this.shipClusterTimeout);
    this.shipClusterTimeout = setTimeout(() => this.syncShipClusters(), 150);
  }

  createShipClusterIcon(ships) {
    return L.divIcon({
      className: 'ship-cluster-wrapper',
      html: shipClusterSvg(ships.map((ship) => ship.shipStatus)),
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });
  }
}

export default MapService;
